import { useState } from "react"
import styled from "styled-components"

import { spacing, Text } from "@minuthq/meatball-ui-react"

import { HomeLogBodyCell } from "src/components/HomeLog/HomeLogContent"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseSearchMultiSelect } from "src/components/Paradise/ParadiseSearchMultiSelect"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { mapFilterGroupsToHomeEventTypes } from "src/data/events/logic/eventsLogic"
import { homeEventFilterOptions } from "src/data/events/logic/homeEventConstants"
import { useFetchHomeEvents } from "src/data/events/queries/homeEventQueries"
import {
  hasDeviceMetaData,
  THomeEventFilterGroup,
  THomeLogEvent,
} from "src/data/events/types/homeEventTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useSearchParams } from "src/router/useSearchParams"
import { DateRangePicker } from "src/ui/DateRangePicker/DateRangePicker"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { LocalizedDate } from "src/ui/LocalizedDate/LocalizedDate"

const LIMIT = 15

export function ParadiseHomeEvents({
  homeId,
  home,
}: {
  homeId: string
  home: { timezone: string; name: string; home_id: string }
}) {
  const [offset, setOffset] = useState(0)

  // Use useSearchParams for eventTypeFilter and dateRange
  const { searchParams, setSearchParams, setSearchParamsAll } = useSearchParams(
    {
      keys: [
        { key: "eventFilter", type: "string", list: true },
        { key: "startDate", type: "string" },
        { key: "endDate", type: "string" },
      ],
    }
  )

  // Convert searchParams to local values
  const eventTypeFilter: THomeEventFilterGroup[] = (searchParams.eventFilter ??
    []) as THomeEventFilterGroup[]
  const startDate = searchParams.startDate
    ? new Date(searchParams.startDate)
    : null
  const endDate = searchParams.endDate ? new Date(searchParams.endDate) : null

  const { t, langKeys } = useTranslate()

  const fetchHomeEvents = useFetchHomeEvents({
    homeId,
    params: {
      limit: LIMIT,
      offset,
      event_type: eventTypeFilter.length
        ? mapFilterGroupsToHomeEventTypes(eventTypeFilter)
        : undefined,
      start_at: startDate ? startDate.toISOString() : undefined,
      end_at: endDate ? endDate.toISOString() : undefined,
    },
    options: {
      keepPreviousData: true,
    },
  })
  const homeEvents = fetchHomeEvents.data?.home_events ?? []

  const tableColumns: TableColumn<THomeLogEvent>[] = [
    {
      label: "Event",
      value: "event",
      renderCell: (event) => (
        <EventCellWrapper>
          <HomeLogBodyCell
            event={event}
            onSmokeFeedbackClicked={() => {}}
            iconSize={24}
            gap={spacing.XS2}
            showFeedback={false}
          />
        </EventCellWrapper>
      ),
      columnWidth: "1fr",
      popoverContent: (
        <ColumnPopoverWrapper>
          <ParadiseSearchMultiSelect
            options={homeEventFilterOptions.map((opt) => ({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              label: t(opt.label as any) ?? opt.label,
              value: opt.value,
            }))}
            selectedValues={eventTypeFilter}
            onChange={(values) => {
              setSearchParams("eventFilter", values.length ? values : null)
              setOffset(0)
            }}
            onSearch={() => {}}
            isLoading={false}
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      label: "Device",
      value: "device",
      renderCell: (event) => {
        const data = event.metadata?.event_specific_data
        if (!hasDeviceMetaData(data)) return <div />
        const fallbackCopy =
          t(langKeys.go_to_generic_text, { name: t(langKeys.sensor) }) ??
          t(langKeys.sensor)
        return (
          <div>
            <InternalLink
              to={Routes.ParadiseDevice.location(data.device_id).pathname}
            >
              {data.device_name ?? fallbackCopy}
            </InternalLink>
          </div>
        )
      },
      columnWidth: "auto",
    },
    {
      label: "Time",
      value: "time",
      renderCell: (event) => (
        <div>
          <Text variant="bodyXS">
            <LocalizedDate date={event.event_time} timezone={home.timezone} />
          </Text>
          <Text variant="bodyXS" color="secondary">
            <LocalizedDate date={event.event_time} timezone="UTC" /> UTC
          </Text>
        </div>
      ),
      columnWidth: "auto",
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: homeEvents,
  })

  const totalCount = fetchHomeEvents.data?.paging.total_count

  return (
    <div>
      <DateRangePickerWrapper>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onSelectPreset={({ clearSelection }) => {
            if (clearSelection) {
              setSearchParamsAll({ startDate: null, endDate: null })
              setOffset(0)
            }
          }}
          onDateRangeChange={({ startDate, endDate }) => {
            setSearchParamsAll({
              startDate: startDate ? startDate.toISOString() : null,
              endDate: endDate ? endDate.toISOString() : null,
            })
            setOffset(0)
          }}
          dateRangeProps={{ minBookingDays: 1 }}
        />
      </DateRangePickerWrapper>
      <ParadiseTable
        header={headerElements}
        rows={rows}
        templateColumns={templateColumns}
        isLoading={fetchHomeEvents.isLoading}
        error={{
          hasError: fetchHomeEvents.isError,
          title: fetchHomeEvents.error?.message,
        }}
      />
      <PagerWrapper>
        <ParadisePager
          limit={LIMIT}
          offset={offset}
          setOffset={setOffset}
          totalCount={totalCount}
        />
      </PagerWrapper>
    </div>
  )
}

// Styled wrapper for event cell to control text overflow and vertical alignment
const EventCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-width: 0;
  max-width: 100%;
  word-break: break-word;
  white-space: normal;
  & > * {
    min-width: 0;
    max-width: 100%;
    word-break: break-word;
    white-space: normal;
  }
`

const DateRangePickerWrapper = styled.div`
  margin-bottom: ${spacing.L};
`

const PagerWrapper = styled.div`
  margin-top: ${spacing.L};
  margin-bottom: ${spacing.XL};
`
